import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getApiClient } from '@apps/web/lib/api_client';

const AVAILABLE_VALUE = 'available';
const UNAVAILABLE_VALUE = 'unavailable';
// Hidden Chrome tabs may run timers only once per minute. The backend lease is
// three minutes; existing websocket presence remains the tighter prerequisite.
const HEARTBEAT_INTERVAL_MS = 60_000;
const STORAGE_KEY_PREFIX = 'phoebe:browser-ring-availability';

type StoredPreference = {
    readonly key: string;
    readonly available: boolean;
};

export type BrowserRingAvailability = {
    readonly isAvailablePreference: boolean;
    readonly isPublishedAvailable: boolean;
    readonly setAvailablePreference: (available: boolean) => void;
};

function preferenceKey(organizationId: string, userId: string): string {
    return `${STORAGE_KEY_PREFIX}:${organizationId}:${userId}`;
}

function readPreference(key: string, value?: string | null): boolean {
    try {
        const storedValue = value ?? window.localStorage.getItem(key);
        return storedValue !== UNAVAILABLE_VALUE;
    } catch {
        // Storage can be unavailable in private browsing. Available remains the
        // compatibility default after an administrator enables the feature.
        return true;
    }
}

async function publishAvailability({
    organizationId,
    connectionId,
    available,
}: {
    readonly organizationId: string;
    readonly connectionId: string;
    readonly available: boolean;
}): Promise<void> {
    await getApiClient().put({
        url: '/organizations/{organization_id}/coordinator-presence/browser-ring-availability',
        pathParams: { organization_id: organizationId },
        body: {
            connection_id: connectionId,
            available,
        },
    });
}

export function useBrowserRingAvailability({
    enabled,
    organizationId,
    userId,
    canReceiveIncomingCall,
}: {
    readonly enabled: boolean;
    readonly organizationId: string | null;
    readonly userId: string | null;
    readonly canReceiveIncomingCall: boolean;
}): BrowserRingAvailability {
    const connectionIdRef = useRef(crypto.randomUUID());
    const availabilityChainRef = useRef<Promise<void>>(Promise.resolve());
    const storageKey = useMemo(
        () =>
            enabled && organizationId && userId
                ? preferenceKey(organizationId, userId)
                : null,
        [enabled, organizationId, userId],
    );
    const [storedPreference, setStoredPreference] =
        useState<StoredPreference | null>(null);
    const [isPublishedAvailable, setIsPublishedAvailable] = useState(false);

    useEffect(() => {
        if (!storageKey) {
            setStoredPreference(null);
            return;
        }

        setStoredPreference({
            key: storageKey,
            available: readPreference(storageKey),
        });
        const handleStorage = (event: StorageEvent) => {
            if (event.key !== storageKey) {
                return;
            }
            setStoredPreference({
                key: storageKey,
                available: readPreference(storageKey, event.newValue),
            });
        };
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, [storageKey]);

    const isAvailablePreference =
        storageKey !== null && storedPreference?.key === storageKey
            ? storedPreference.available
            : false;

    const setAvailablePreference = useCallback(
        (available: boolean) => {
            if (!storageKey) {
                return;
            }
            setStoredPreference({ key: storageKey, available });
            try {
                window.localStorage.setItem(
                    storageKey,
                    available ? AVAILABLE_VALUE : UNAVAILABLE_VALUE,
                );
            } catch {
                // The in-memory choice remains useful for this tab even when
                // storage is unavailable.
            }
        },
        [storageKey],
    );

    useEffect(() => {
        setIsPublishedAvailable(false);
        if (
            !enabled ||
            !organizationId ||
            !userId ||
            !canReceiveIncomingCall ||
            !isAvailablePreference
        ) {
            return;
        }

        let active = true;
        const connectionId = connectionIdRef.current;

        const queueHeartbeat = () => {
            availabilityChainRef.current = availabilityChainRef.current
                .then(async () => {
                    if (!active) {
                        return;
                    }
                    await publishAvailability({
                        organizationId,
                        connectionId,
                        available: true,
                    });
                    if (active) {
                        setIsPublishedAvailable(true);
                    }
                })
                .catch((error: unknown) => {
                    if (active) {
                        console.error(
                            'Failed to publish browser call availability:',
                            error,
                        );
                        setIsPublishedAvailable(false);
                    }
                });
        };

        queueHeartbeat();
        const heartbeatInterval = window.setInterval(
            queueHeartbeat,
            HEARTBEAT_INTERVAL_MS,
        );

        return () => {
            active = false;
            window.clearInterval(heartbeatInterval);
            setIsPublishedAvailable(false);
            // Serialize cleanup behind any in-flight heartbeat. Otherwise a
            // slow request or a busy-to-idle effect restart can reorder writes
            // for this connection and publish the opposite of its real state.
            availabilityChainRef.current = availabilityChainRef.current
                .then(async () => {
                    await publishAvailability({
                        organizationId,
                        connectionId,
                        available: false,
                    });
                })
                .catch((error: unknown) => {
                    console.error(
                        'Failed to clear browser call availability:',
                        error,
                    );
                });
        };
    }, [
        canReceiveIncomingCall,
        enabled,
        isAvailablePreference,
        organizationId,
        userId,
    ]);

    return {
        isAvailablePreference,
        isPublishedAvailable,
        setAvailablePreference,
    };
}
